interface RateLimitData {
  attempts: number;
  resetTime: number;
  lockedUntil?: number;
}

const RATE_LIMIT_STORAGE_KEY = 'fusion_mart_rate_limits';

// Rate limit configuration interface
interface RateLimitConfig {
  maxAttempts: number;
  windowMs: number;
  lockoutMs?: number;
}

// Default rate limit configurations
const RATE_LIMITS: Record<string, RateLimitConfig> = {
  login: {
    maxAttempts: 5,
    windowMs: 15 * 60 * 1000, // 15 minutes
    lockoutMs: 30 * 60 * 1000, // 30 minutes lockout after max attempts
  },
  formSubmission: {
    maxAttempts: 10,
    windowMs: 60 * 1000, // 1 minute
  },
  apiCall: {
    maxAttempts: 100,
    windowMs: 60 * 1000, // 1 minute
  },
};

const getRateLimitData = (key: string): RateLimitData | null => {
  try {
    const stored = localStorage.getItem(`${RATE_LIMIT_STORAGE_KEY}_${key}`);
    if (!stored) return null;
    const data: RateLimitData = JSON.parse(stored);
    
    // Check if the window has expired
    if (Date.now() > data.resetTime) {
      localStorage.removeItem(`${RATE_LIMIT_STORAGE_KEY}_${key}`);
      return null;
    }
    
    // Check if still locked
    if (data.lockedUntil && Date.now() < data.lockedUntil) {
      return data;
    }
    
    // Clear lockout if expired
    if (data.lockedUntil && Date.now() >= data.lockedUntil) {
      const newData: RateLimitData = {
        attempts: 0,
        resetTime: data.resetTime,
      };
      localStorage.setItem(`${RATE_LIMIT_STORAGE_KEY}_${key}`, JSON.stringify(newData));
      return newData;
    }
    
    return data;
  } catch (error) {
    console.error('Error reading rate limit data:', error);
    return null;
  }
};

const setRateLimitData = (key: string, data: RateLimitData): void => {
  try {
    localStorage.setItem(`${RATE_LIMIT_STORAGE_KEY}_${key}`, JSON.stringify(data));
  } catch (error) {
    console.error('Error setting rate limit data:', error);
  }
};

export const checkRateLimit = (type: string, identifier: string = 'default'): {
  allowed: boolean;
  remainingAttempts: number;
  resetTime: number;
  lockedUntil?: number;
  message?: string;
} => {
  const limit = RATE_LIMITS[type];
  const key = `${type}_${identifier}`;
  const now = Date.now();
  
  let data = getRateLimitData(key);
  
  // Check if locked out
  if (data?.lockedUntil && now < data.lockedUntil) {
    const minutesRemaining = Math.ceil((data.lockedUntil - now) / 60000);
    return {
      allowed: false,
      remainingAttempts: 0,
      resetTime: data.resetTime,
      lockedUntil: data.lockedUntil,
      message: `Too many failed attempts. Please try again in ${minutesRemaining} minute${minutesRemaining > 1 ? 's' : ''}.`,
    };
  }
  
  // Initialize or reset if window expired
  if (!data || now >= data.resetTime) {
    data = {
      attempts: 0,
      resetTime: now + limit.windowMs,
    };
  }
  
  // Check if limit exceeded
  if (data.attempts >= limit.maxAttempts) {
    const lockedUntil = now + (limit.lockoutMs ?? 0);
    const updatedData: RateLimitData = {
      ...data,
      lockedUntil,
    };
    setRateLimitData(key, updatedData);
    
    const minutesRemaining = Math.ceil((lockedUntil - now) / 60000);
    return {
      allowed: false,
      remainingAttempts: 0,
      resetTime: data.resetTime,
      lockedUntil,
      message: `Rate limit exceeded. Locked for ${minutesRemaining} minute${minutesRemaining > 1 ? 's' : ''}.`,
    };
  }
  
  // Increment attempts
  data.attempts += 1;
  setRateLimitData(key, data);
  
  return {
    allowed: true,
    remainingAttempts: limit.maxAttempts - data.attempts,
    resetTime: data.resetTime,
  };
};

export const resetRateLimit = (type: string, identifier: string = 'default'): void => {
  const key = `${type}_${identifier}`;
  localStorage.removeItem(`${RATE_LIMIT_STORAGE_KEY}_${key}`);
};

export const getRemainingTime = (resetTime: number): number => {
  const remaining = resetTime - Date.now();
  return remaining > 0 ? remaining : 0;
};

export const formatRemainingTime = (ms: number): string => {
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
};
